/**
 * Add Student — bulk future assignment (spec §5). Adds the selected student
 * to the class-level roster and all applicable future occurrences. This is
 * NOT a one-session assignment; that lives on the Class Sessions page.
 */
import React, { useMemo, useState } from "react";
import {
  Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Stack, TextField, Typography, alpha, useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { useNotifier } from "../../../../../Core/Notifier";
import {
  useGetClassScheduleEligibleStudentsQuery,
  useAddClassScheduleStudentMutation,
} from "../../../../../Api/attoDeskApi";
import type { SchedulePattern } from "../ClassSchedulesTab";

interface Props {
  open: boolean;
  pattern: SchedulePattern;
  onClose: () => void;
  onDone: () => void;
}

const AddStudentDialog: React.FC<Props> = ({ open, pattern, onClose, onDone }) => {
  const theme = useTheme();
  const { showMessage, showErrorMessage } = useNotifier();
  const [studentId, setStudentId] = useState<number | null>(null);
  const [effectiveDate, setEffectiveDate] = useState<string>(dayjs().format("YYYY-MM-DD"));

  const { data: eligibleData, isFetching } = useGetClassScheduleEligibleStudentsQuery({ scheduleId: pattern.schedule_id });
  const [addStudent, { isLoading: saving }] = useAddClassScheduleStudentMutation();

  const students = useMemo(
    () => (Array.isArray((eligibleData as any)?.data) ? (eligibleData as any).data as Array<{ id: number; name: string }> : []),
    [eligibleData],
  );

  const handleSubmit = async () => {
    if (!studentId) { showErrorMessage("Select a student."); return; }
    try {
      const res = await addStudent({
        scheduleId: pattern.schedule_id,
        student_id: studentId,
        effective_date: effectiveDate,
      }).unwrap();
      if ((res as any)?.status !== false) {
        showMessage((res as any)?.message || "Student added to class schedule.");
        onDone();
      } else {
        showErrorMessage((res as any)?.message || "Failed to add student.");
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Failed to add student.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
      <DialogTitle sx={{ pr: 6, fontSize: 16, fontWeight: 700 }}>
        Add Student to Class Schedule
        <IconButton onClick={onClose} size="small" sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <ScheduleSummary pattern={pattern} />
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          <Autocomplete
            options={students}
            loading={isFetching}
            getOptionLabel={(o) => o.name}
            value={students.find((s) => s.id === studentId) || null}
            onChange={(_, val) => setStudentId(val ? val.id : null)}
            renderInput={(params) => <TextField {...params} label="Student" size="small" placeholder="Search eligible students…" />}
            isOptionEqualToValue={(a, b) => a.id === b.id}
          />
          <TextField
            label="Effective start date" type="date" size="small"
            value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Applies to all future occurrences on or after this date."
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving || !studentId}
          sx={{ textTransform: "none", boxShadow: "none", borderRadius: "8px" }}>
          {saving ? "Adding…" : "Add Student"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ScheduleSummary: React.FC<{ pattern: SchedulePattern }> = ({ pattern }) => {
  const theme = useTheme();
  return (
    <Box sx={{ p: 1.5, borderRadius: "10px", bgcolor: alpha(theme.palette.primary.main, 0.05), border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}` }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{pattern.day} · {pattern.start_time} – {pattern.end_time}</Typography>
      {pattern.staff && (
        <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.25 }}>Staff: {pattern.staff.name}</Typography>
      )}
      <Box sx={{ mt: 0.5 }}>
        {pattern.grade_subjects.map((g) => (
          <Stack key={g.grade_id} direction="row" spacing={0.5} sx={{ flexWrap: "wrap", alignItems: "center", mt: 0.25 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, minWidth: 56 }}>{g.grade}</Typography>
            {g.subjects.map((s) => <Chip key={s.id} label={s.name} size="small" sx={{ height: 18, fontSize: 10 }} />)}
          </Stack>
        ))}
      </Box>
      <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.5 }}>
        {pattern.capacity > 0 ? `${pattern.enrolled}/${pattern.capacity} enrolled` : `${pattern.enrolled} enrolled · Capacity not set`}
        {pattern.study_mode ? ` · ${pattern.study_mode}` : ""}
        {pattern.location?.name ? ` · ${pattern.location.name}` : ""}
      </Typography>
    </Box>
  );
};

export default AddStudentDialog;
